(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else root.LiliCommerce = api;
})(typeof window === 'undefined' ? globalThis : window, function () {
  'use strict';
  const isColor = name => /^(color|colour|колір|цвет)$/iu.test(String(name).trim());
  const work = product => product?.work?.version === 1 ? product.work : product?.legacyWork?.version === 1 ? product.legacyWork : {};
  function options(product) {
    const list = product.options || [];
    return list.map((option, index) => ({name: typeof option === 'string' ? option : option.name,
      values: (typeof option === 'object' && option.values?.length ? option.values : [...new Set((product.variants || []).map(v => v.options?.[index]).filter(Boolean))]).map(value => typeof value === 'object' ? value.name : value), index}))
      .filter(option => option.name && !(option.values.length === 1 && option.values[0] === 'Default Title'));
  }
  function resolveVariant(product, selected) {
    const names = options(product);
    return (product.variants || []).find(variant => names.every(option => variant.options?.[option.index] === selected[option.index])) || null;
  }
  function selectionExists(product, selected, index, value) {
    return (product.variants || []).some(variant => options(product).every(option => variant.options?.[option.index] === (option.index === index ? value : selected[option.index])));
  }
  function colors(product) {
    const option = colorOption(product);
    const supplied = work(product).colors || [];
    const rawValues = product.options?.[option?.index]?.values || [];
    return option ? option.values.map((name,index) => {
      const id = rawValues[index]?.id;
      const match = id != null ? supplied.find(color => color.optionValueId != null && String(color.optionValueId) === String(id)) : null;
      return {...(match || supplied.find(color => color.name === name)), name};
    }) : supplied;
  }
  function colorOption(product) {
    const index = work(product).colorOptionIndex;
    return options(product).find(option => Number.isInteger(index) ? option.index === index : isColor(option.name));
  }
  function primaryColor(product) {
    const data = work(product);
    return colors(product).find(color => color.id != null && String(color.id) === String(data.primaryColorId))?.name || data.primaryColor;
  }
  function availability(product, variant) {
    // Tracked Shopify inventory is live; imported Work quantities are a snapshot.
    if (variant?.inventoryManagement && ['deny','continue'].includes(variant.inventoryPolicy)) {
      if (variant.inventoryQuantity > 0) return 'in_stock';
      return variant.inventoryPolicy === 'continue' ? 'made_to_order' : 'unavailable';
    }
    const status = variant?.work?.availability || work(product).availability;
    if (work(product).kind === 'gift_certificate' && status === 'in_stock' && variant?.available !== false) return 'in_stock';
    if (status === 'in_stock' && (variant?.work?.quantity ?? work(product).quantity ?? 0) > 0) return 'in_stock';
    if (status === 'made_to_order') return 'made_to_order';
    if (status) return 'unavailable';
    if (product.madeToOrder) return 'made_to_order';
    if (product.stockUnconfirmed) return 'unknown';
    if (variant?.inventoryManagement && variant.inventoryQuantity > 0) return 'in_stock';
    return variant?.available === false || product.available === false ? 'unavailable' : 'unknown';
  }
  function availabilityValues(product) {
    const explicit = work(product).availabilityFilters;
    if (Array.isArray(explicit) && explicit.length) return [...new Set(explicit.map(item => typeof item === 'string' ? item : item.code).map(code => code?.startsWith('production_') ? 'made_to_order' : code).filter(code => ['in_stock','made_to_order'].includes(code)))];
    return [...new Set((product.variants?.length ? product.variants : [null]).map(variant => availability(product, variant)))];
  }
  function price(product, variant, currency, native, now = Date.now()) {
    const source = variant || product;
    if ((variant ? variant.work?.priceMissing : work(product).priceMissing) === true) return {amount:undefined,compare:undefined};
    const marketKey = {UAH:'ukraine',USD:'international',EUR:'international_eur'}[currency];
    const market = (source.work?.marketPrices || work(product).marketPrices)?.[marketKey];
    const promotion = work(product).discount;
    const end = promotion?.endsAt ? Date.parse(promotion.endsAt) : null;
    // Shopify compare-at fields can remain cached until the next sync; expiry only hides the old price.
    const compare = value => promotion?.active === false || end !== null && (!Number.isFinite(end) || now >= end) ? undefined : value;
    // A live cart must always receive the server's presentment price, never a client conversion.
    if (native) return source.currency && source.currency !== currency ? {amount:undefined,compare:undefined} : {amount:source.price, compare:compare(source.compareAtPrice)};
    if (work(product).version === 1) return {amount:market?.price_minor, compare:compare(market?.compare_at_price_minor)};
    return {amount:source.currency === currency ? source.price : undefined, compare:source.currency === currency ? compare(source.compareAtPrice) : undefined};
  }
  function discount(product, variant, currency, native, now = Date.now()) {
    const prices = price(product, variant, currency, native, now);
    const data = work(product).discount;
    const end = data?.endsAt ? Date.parse(data.endsAt) : null;
    const active = prices.amount > 0 && prices.compare > prices.amount && data?.active !== false && (end === null || Number.isFinite(end) && now < end);
    return {active, percent:active ? Math.round((1-prices.amount/prices.compare)*100) : 0, endsAt:active && Number.isFinite(end) ? end : null};
  }
  function countdown(end, now = Date.now()) {
    const seconds = Math.max(0, Math.ceil((end - now)/1000));
    return {expired:seconds === 0, days:Math.floor(seconds/86400), hours:Math.floor(seconds%86400/3600), minutes:Math.floor(seconds%3600/60), seconds:seconds%60};
  }
  function safeURL(value) { return /^(https?:\/\/|\/(?!\/))/i.test(String(value || '')) ? value : ''; }
  // Storefront currency is independent of the payment provider's checkout currency.
  const euCountries = new Set('AT BE BG HR CY CZ DK EE FI FR DE GR HU IE IT LV LT LU MT NL PL PT RO SK SI ES SE'.split(' '));
  function displayCurrency(region, country, saved) {
    if (region === 'ukraine') return 'UAH';
    return ['USD','EUR'].includes(saved) ? saved : euCountries.has(country) ? 'EUR' : 'USD';
  }
  function usesDisplayCurrency(native, region, checkoutCurrency) {
    return !native || region === 'international' || region === 'ukraine' && checkoutCurrency === 'USD';
  }
  function cartDisplay(cart, currency) {
    if (cart.currency === currency) return {total:cart.total_price, lines:cart.items.map(item=>item.final_line_price)};
    const key = {USD:'international',EUR:'international_eur',UAH:'ukraine'}[currency];
    const baseKey = {USD:'international',EUR:'international_eur',UAH:'ukraine'}[cart.currency];
    const lines = cart.items.map(item=>{
      const prices=item.work?.marketPrices;
      const base=prices?.[baseKey]?.price_minor, target=prices?.[key]?.price_minor;
      if (!Number.isFinite(base)||base<=0||!Number.isFinite(target)||target<0) return undefined;
      // Keep Shopify's line discounts and selling-plan adjustments in the display amount.
      return Math.round(item.final_line_price*target/base);
    });
    if (lines.some(value=>!Number.isFinite(value))) return {total:undefined,lines};
    const beforeCartDiscount=cart.items.reduce((sum,item)=>sum+item.final_line_price,0);
    const subtotal=lines.reduce((sum,value)=>sum+value,0);
    const total=beforeCartDiscount>0 ? Math.round(subtotal*cart.total_price/beforeCartDiscount) : 0;
    return {total,lines};
  }
  // A region can be selected before its payment currencies are configured.
  // Currency changes, however, must resolve to a real Shopify presentment currency.
  function regionCountry(countries, region, currency, current) {
    if (!['ukraine','international'].includes(region)) return null;
    const eligible = countries.filter(item => (region === 'ukraine' ? item.id === 'UA' : item.id !== 'UA') && (!currency || item.currency === currency));
    return eligible.find(item => item.id === current) || eligible.find(item => item.id === (currency === 'EUR' ? 'DE' : 'US')) || eligible[0] || null;
  }
  return {work, options, colors, colorOption, primaryColor, isColor, resolveVariant, selectionExists, availability, availabilityValues, price, discount, countdown, safeURL, regionCountry, displayCurrency, usesDisplayCurrency, cartDisplay};
});
